/** 게시판 화면이 쓰는 값 — 서버 `domain/board` 응답과 같은 모양이다 */

/** 게시판 유형 — 화면 구조를 정한다(2026-09-01 확정. 권한·알림 차이는 없다) */
export type BoardCategory =
  | 'notice' | 'faq' | 'help' | 'review'
  | 'general' | 'comment'
  | 'teacher_notice' | 'teacher_qna'
  | (string & {});

export interface BoardSetting {
  id: number;
  category: BoardCategory | null;
  name: Record<string, string> | null;
  teacherId: number | null;
  secretEnabled: boolean;
  editorEnabled: boolean;
  /** 한 쪽 분량 — 게시판 설정(기본 10). 실제로 쓰인 값은 응답의 `size` 다 */
  pageSize: number;
  uploadCount: number | null;
  uploadSize: number | null;
  /** 목록에 본인 글만 보이는 게시판인가 — 걸러 내는 일은 **서버가 이미 했다** */
  ownerOnly: boolean;
  /** 댓글을 쓰는 게시판인가 — 끄면 화면이 댓글 자리를 아예 그리지 않는다 */
  commentEnabled: boolean;
  /** 글을 쓸 수 있나 — 게시판 설정(`writeRole`)을 서버가 판정해 준다 */
  canWrite: boolean;
  /** 상단 고정 공지로 쓸 수 있나 — 공지글 사용 ∧ 관리자. 서버가 판정한다 */
  canNotice: boolean;
}

export type BoardStatus = 'IN_PROGRESS' | 'COMPLETED' | 'ADDITIONAL' | null;

export interface BoardRow {
  id: number;
  subject: string | null;
  writer: string | null;
  isNotice: boolean;
  isSecret: boolean;
  status: BoardStatus;
  groupId: number | null;
  groupOrder: number | null;
  viewCount: number;
  starRating: number | null;
  /** FAQ 분류 — 코드표 `faq_cate` 값 */
  faqCategory: number | null;
  /** 문의 유형 — 코드표 값(1:1은 qna_option, 강사 Q&A는 tq_option) */
  qnaOption: number | null;
  productId: number | null;
  teacherId: number | null;
  createdAt: string;
  commentCount: number;
  /** 작성자 이름 — 목록에서도 이름(아이디)로 적는다 */
  writerName: string | null;
  /** 고칠 수 있나 — 작성자·관리자. **서버가 판정한다**(목록 줄에서도 쓴다) */
  canEdit: boolean;
}

export interface BoardList {
  setting: BoardSetting;
  /** 상단 고정 — 페이지를 넘겨도 늘 위에 있다 */
  notices: BoardRow[];
  rows: BoardRow[];
  total: number;
  page: number;
  size: number;
}

export interface BoardComment {
  id: number;
  writer: string | null;
  name: string | null;
  comment: string | null;
  createdAt: string;
  /** 고칠 수 있나 — 본인만(관리자도 남의 말은 못 고친다). 서버가 판정한다 */
  canEdit: boolean;
  /** 지울 수 있나 — **서버가 판정한다**(본인·관리자). 화면이 다시 셈하지 않는다 */
  canDelete: boolean;
}

export interface BoardReply {
  id: number;
  writer: string | null;
  writerName: string | null;
  content: string | null;
  createdAt: string;
}

/** 첨부 한 개 — 서버가 `{ url, name, size }` 로 준다(옛 글의 파일명 배열도 서버가 맞춰 준다) */
export interface BoardFile {
  url: string;
  name: string;
  size: number;
}

export interface BoardPost extends BoardRow {
  writerName: string | null;
  content: string | null;
  /** 첨부 — 게시판 설정이 첨부를 안 받으면 빈 배열이다 */
  attachments: BoardFile[];
  canEdit: boolean;
  /** 댓글 자리를 그리나 — 게시판 설정이 끄면 목록도 입력칸도 없다 */
  commentEnabled: boolean;
  /** 댓글을 쓸 수 있나 — 사용 여부 ∧ 권한(`commentRole`)을 서버가 판정해 준다 */
  canComment: boolean;
  replies: BoardReply[];
  comments: BoardComment[];
}


/**
 * 아이디 가리기 — 가이드가 `한석봉(wjj****)` 꼴로 적는다.
 * 앞 세 글자만 남긴다(현행도 같은 규칙). 짧은 아이디는 남는 만큼만 보인다.
 */
export function maskUid(uid: string | null | undefined): string {
  if (!uid) return '';
  return `${uid.slice(0, 3)}${'*'.repeat(Math.max(uid.length - 3, 0)) || ''}`;
}

/**
 * 이름 가리기 — **가운데를 가린다**(2026-09-01 사용자 지시 "이름도 마스킹").
 * `킹삿갓` → `킹*갓` · `홍길동` → `홍*동` · 두 글자는 뒤를 가린다(`김철` → `김*`).
 * 아이디는 앞 세 자만 남기지만(`maskUid`) 이름은 성과 끝 자를 남긴다 — 그래야 누가 썼는지
 * 짐작은 되면서 온전히 드러나지는 않는다(현행·업계 관례).
 */
export function maskName(name: string | null | undefined): string {
  const n = (name ?? '').trim();
  if (!n) return '';
  if (n.length <= 1) return n;
  if (n.length === 2) return `${n[0]}*`;
  return `${n[0]}${'*'.repeat(n.length - 2)}${n[n.length - 1]}`;
}

/** `2026.08.01` — 목록·머리글의 날짜 표기(가이드 그대로) */
export function boardDate(v: string | null | undefined): string {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10).replace(/-/g, '.');
  return d.toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' }).replace(/-/g, '.');
}

/**
 * `2026.08.01 15:28:07` — **상세 뷰·댓글처럼 시각이 필요한 자리**(2026-09-08 사용자 확정: 목록은 날짜만,
 * 상세는 초까지). 초까지 적는 까닭은 같은 분에 글이 여럿일 때 순서를 알아볼 수 있어서다.
 */
export function boardStamp(v: string | null | undefined): string {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 19).replace('T', ' ');
  return d.toLocaleString('sv-SE', { timeZone: 'Asia/Seoul' }).slice(0, 19).replace(/-/g, '.');
}

/** 새 글 표시 — 가이드 `bbs-new`. 사흘 안에 쓴 글에 붙인다 */
export function isNew(v: string | null | undefined): boolean {
  if (!v) return false;
  const d = new Date(v).getTime();
  return Number.isFinite(d) && Date.now() - d < 3 * 24 * 60 * 60 * 1000;
}
